<template>
  <div :class="[is_pinned ? 'relative h-full' : 'flex h-full']">
    <div
      v-if="!is_pinned"
      class="fixed inset-0 z-40"
      @click="closeSidebar"
    />
    <div
      id="project-sidebar"
      class="z-40 flex h-full bg-white shadow-lg"
      :class="[is_pinned ? '' : 'absolute top-0 left-0 z-50']"
      :style="{ width: mq.isMobile ? '100%' : sidebar_width / 16 + 'rem' }"
      data-test="employee-project-sidebar"
    >
      <div class="relative flex w-full flex-col gap-4 pt-3">
        <SidebarHeader
          :title="$t('navbar.employee.projects')"
          :is_pinned="is_pinned"
          side="left"
          data_test="projects"
          @close="closeSidebar"
          @toggle-pin="UPDATE_PROJECT_SIDEBAR_PREFS({ is_pinned: !is_pinned })"
        />

        <SelectProject
          class="px-3"
          :autofocus="true"
          :is_dropdown="false"
          :label="$t('project.labels.searchProject')"
          @changeSelect="(q) => (search_project = q.attributes.name)"
          @onSearch="(q) => (search_project = q)"
        />

        <AppLink
          v-if="show_all_projects"
          :to="overview_to"
          hide_underline
          class="group mx-3 flex items-center rounded-full bg-surface-50 p-2 text-center text-surface-600 transition-colors duration-200 hover:bg-surface-100"
          @click="handleClose"
        >
          <div class="grow truncate text-surface-700">
            {{ $t('navbar.employee.all_projects') }}
          </div>
        </AppLink>

        <div class="flex-1 overflow-auto">
          <ProjectSection
            v-for="section in projectSections"
            :key="section.title"
            v-bind="section.props"
            v-on="section.events"
          />
        </div>
        <div
          v-if="current_profile.attributes.role === Employee.ROLE_OWNER"
          class="mt-auto shrink-0"
        >
          <div class="mx-4 mb-2">
            <ButtonPrimary
              action="create"
              class="mb-3 w-full"
              data-test="create-project-new"
              @onClick="goToProjectCreate"
            >
              {{ $t('project.labels.project') }}
            </ButtonPrimary>
          </div>
        </div>
      </div>
      <div
        v-if="!mq.isMobile"
        class="z-50 h-full w-[0.10rem] cursor-col-resize transition-colors duration-200 hover:bg-primary-500"
        :class="{
          'bg-red-300': is_resizing,
          'absolute top-0 right-0': is_pinned,
        }"
        @mousedown="start_resize"
      />
    </div>
    <div
      v-if="!mq.isMobile"
      class="z-50 h-full w-[0.10rem] cursor-col-resize transition-colors duration-200 hover:bg-primary-500"
      :class="{
        'bg-red-300': is_resizing,
        'absolute top-0 right-0': is_pinned,
      }"
      @mousedown="start_resize"
    />

    <PrimeModal
      :has_parent="false"
      :is_small="false"
      :is_medium="true"
      :hide_maximize_button="true"
      v-if="limit_reached_modal"
      @action-cancel="limit_reached_modal = false"
      @action-primary="openIntercom"
    >
      <template #title>{{ $t('project.labels.limitReached') }}</template>
      <template #content>
        <div
          class="mx-auto flex max-w-3xl flex-col items-center gap-6 rounded-lg border-2 border-primary-500 bg-orange-50 p-8 py-4"
        >
          <div class="flex flex-col items-center justify-center gap-4 sm:flex-row sm:items-center">
            <div class="shrink-0">
              <img
                class="h-32 w-32 object-contain sm:h-40 sm:w-40"
                :src="Ziggy_laptop"
                alt="Ziggy with laptop"
              />
            </div>
            <div class="flex flex-1 flex-col justify-center gap-2 text-center sm:text-left">
              <p class="font-medium text-surface-700">
                {{ $t('project.labels.outOfProjects') }}
              </p>
              <p class="text-sm text-surface-600">
                {{ $t('project.labels.outOfProjectsContact') }}
              </p>
            </div>
          </div>
        </div>
      </template>
      <template #ButtonPrimary>{{ $t('modal.actions.contact_ziggu') }}</template>
    </PrimeModal>
  </div>
</template>

<script setup>
import { computed, getCurrentInstance, provide, ref, watch } from 'vue';
import { useStore } from 'vuex';
import { ROUTES_EMPLOYEE_OVERVIEWS } from '@/app/data/route_constants';
import { getEmployeeOverviewRouteName } from '@/app/data/sidebar_constants';
import { Assignment, Employee, LEVEL_TYPE, Project, PROJECT_TYPES } from '@/app/data/model_constants';
import TheProjectModal from '@/app/_employees/components/projects/form/TheProjectModal.vue';
import SelectProject from '@/app/shared_components/selects/SelectProject.vue';
import SidebarHeader from '@/app/shared_components/sidebar/SidebarHeader.vue';
import ProjectSection from './ProjectSection.vue';
import { useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import Ziggy_laptop from '@/assets/images/ziggy/Ziggy_laptop.png';
import PrimeModal from '@/app/shared_components/modals/ThePrimeModal.vue';
import { useMqService } from '@/plugins/mq';
import useSidebarResize from '@/composables/useSidebarResize';
import { useToasts } from '@/app/util/toasts';

const { t } = useI18n();
const { toast_action_failed } = useToasts();

const { proxy: app } = getCurrentInstance();

const route = useRoute();
const store = useStore();
const mq = useMqService();

const active_popover_project_id = ref(null);
provide('active_popover_project_id', active_popover_project_id);

const project_sidebar_prefs = computed(() => store.getters.project_sidebar_prefs);
const is_visible = computed(() => store.getters.project_sidebar_visible);
const sidebar_width = computed(() => project_sidebar_prefs.value.sidebar_width);
const is_pinned = computed(() => {
  if (mq.isMobile) return false;
  return project_sidebar_prefs.value.is_pinned;
});

const UPDATE_PROJECT_SIDEBAR_PREFS = (payload) => store.dispatch('UPDATE_PROJECT_SIDEBAR_PREFS', payload);

const { is_resizing, start_resize } = useSidebarResize({
  sidebar_width,
  direction: 'right',
  onResize: (width) => UPDATE_PROJECT_SIDEBAR_PREFS({ sidebar_width: width }),
});

const closeSidebar = () => store.dispatch('SHOW_PROJECT_SIDEBAR', false);

const handleClose = () => {
  if (!is_pinned.value) closeSidebar();
};

const assignments = ref([]);
const project_ids_follow_pending = ref({});
const loaded_other_projects = ref(false);
const loaded_project_templates = ref(false);
const search_project = ref('');
const toggle_projects_follow = ref(true);
const toggle_projects_other = ref(false);
const toggle_projects_template = ref(false);
const is_fetching_other = ref(false);
const is_fetching_template = ref(false);
const limit_reached_modal = ref(false);
const projects_all = computed(() => store.getters.projects);
const current_profile = computed(() => store.getters.current_profile);
const current_client = computed(() => store.getters.current_client);

function filterProjects(projects, filterFn) {
  const projectArray = Array.isArray(projects) ? projects : Object.values(projects);

  const search = search_project.value?.toLowerCase();

  return projectArray
    .filter((project) => {
      if (!filterFn(project)) return false;
      if (search) {
        const name = project.attributes.name?.toLowerCase() || '';
        return name.includes(search);
      }
      return true;
    })
    .sort((a, b) => (a.attributes.name || '').localeCompare(b.attributes.name || ''));
}

const projects_follow = computed(() =>
  filterProjects(
    _.pick(projects_all.value, _.map(assignments.value, 'attributes.assigned_id')),
    (project) => project.attributes.status === Project.STATUS_ENABLED && !project.attributes.skip_customer,
  ),
);

const projects_other = computed(() =>
  filterProjects(
    _.omit(projects_all.value, _.map(assignments.value, 'attributes.assigned_id')),
    (project) => project.attributes.status === Project.STATUS_ENABLED && !project.attributes.skip_customer,
  ),
);

const projects_template = computed(() =>
  filterProjects(projects_all.value, (project) => project.attributes.skip_customer),
);

const show_all_projects = computed(
  () =>
    app.$flipper.enabled(app.$FT.TODOS) ||
    app.$flipper.enabled(app.$FT.DOCUMENTS) ||
    app.$flipper.enabled(app.$FT.DECISIONS_CORE),
);

const overview_route_name_default = computed(() => {
  if (app.$flipper.enabled(app.$FT.TODOS)) {
    return ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_TASKS;
  }
  if (app.$flipper.enabled(app.$FT.DOCUMENTS)) {
    return ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_DOCUMENTS;
  }
  return ROUTES_EMPLOYEE_OVERVIEWS.OVERVIEW_DECISIONS;
});

const overview_to = computed(() => {
  const route_name = getEmployeeOverviewRouteName(route.name, overview_route_name_default.value);

  return { name: route_name };
});

const remaining_unit_count = computed(
  () => current_client.value.attributes.units_active_count_max - current_client.value.attributes.units_active_count,
);

const openIntercom = () => {
  window.Intercom('show');
  limit_reached_modal.value = false;
};

watch(search_project, () => {
  if (!search_project.value) return;
  toggle_projects_other.value = _.size(projects_other.value) > 0;
  toggle_projects_follow.value = _.size(projects_follow.value) > 0;
  toggle_projects_template.value = _.size(projects_template.value) > 0;
});

watch(
  () => route.path,
  () => {
    if (is_visible.value && !mq.isDesktop && !is_pinned.value) {
      closeSidebar();
    }
  },
  { immediate: true },
);

watch(
  is_visible,
  async (val) => {
    if (val) {
      search_project.value = '';
      const [assignments_fetched] = await Promise.all([
        store.dispatch('FETCH_ASSIGNMENTS', {
          assigned_type: LEVEL_TYPE.PROJECT,
          assignee_type: current_profile.value.type,
          assignee_id: current_profile.value.id,
          assignment_type: Assignment.TYPE_FOLLOW,
        }),
        store.dispatch('FETCH_PROJECTS', { followed: true }),
      ]);
      assignments.value = assignments_fetched;
    }
  },
  { immediate: true },
);

async function createAssignment(assignment) {
  const project_id = String(assignment.attributes.assigned_id);
  const assignment_existing = assignments.value.find(
    (assignment_item) =>
      assignment_item.attributes.assigned_id === project_id &&
      assignment_item.attributes.assignee_id === String(current_profile.value.id) &&
      assignment_item.attributes.assignee_type === current_profile.value.type &&
      assignment_item.attributes.assignment_type === Assignment.TYPE_FOLLOW,
  );
  if (assignment_existing || project_ids_follow_pending.value[project_id]) return;

  project_ids_follow_pending.value = {
    ...project_ids_follow_pending.value,
    [project_id]: true,
  };

  try {
    const assignment_new = await store.dispatch('CREATE_ASSIGNMENT', {
      record: assignment,
    });
    const assignment_added = assignments.value.some((assignment_item) => assignment_item.id === assignment_new.id);
    if (!assignment_added) assignments.value.push(assignment_new);
  } catch (error) {
    toast_action_failed({ error });
  } finally {
    const { [project_id]: _project_id_ignored, ...project_ids_follow_pending_next } = project_ids_follow_pending.value;
    project_ids_follow_pending.value = project_ids_follow_pending_next;
  }
}

async function deleteAssignment(assignment) {
  await store.dispatch('DELETE_ASSIGNMENT', { record: assignment });
  assignments.value = assignments.value.filter((a) => a.id !== assignment.id);
}

async function goToProjectCreate() {
  if (app.$flipper.enabled(app.$FT.TEMPLATE_PROJECTS) && !loaded_project_templates.value) {
    await store.dispatch('FETCH_PROJECTS', { skip_customer: true });
    loaded_project_templates.value = true;
  }
  handleClose();
  if (current_client.value.attributes.project_type === PROJECT_TYPES.SUP && remaining_unit_count.value <= 0) {
    limit_reached_modal.value = true;
  } else {
    app.$buefy.modal.open({
      component: TheProjectModal,
    });
  }
}

async function toggleOtherProjects() {
  is_fetching_other.value = true;
  toggle_projects_other.value = !toggle_projects_other.value;
  if (!loaded_other_projects.value) {
    await store.dispatch('FETCH_PROJECTS', {
      skip_customer: false,
      followed: false,
    });
    loaded_other_projects.value = true;
  }
  is_fetching_other.value = false;
}

async function toggleProjectTemplates() {
  is_fetching_template.value = true;
  toggle_projects_template.value = !toggle_projects_template.value;
  if (!loaded_project_templates.value) {
    await store.dispatch('FETCH_PROJECTS', { skip_customer: true });
    loaded_project_templates.value = true;
  }
  is_fetching_template.value = false;
}

const projectSections = computed(() => [
  {
    title: t('project.labels.followedProjects'),
    props: {
      title: t('project.labels.followedProjects'),
      is_open: toggle_projects_follow.value,
      on_toggle: () => {
        toggle_projects_follow.value = !toggle_projects_follow.value;
      },
      projects: projects_follow.value,
      item_props: {
        assignments: assignments.value,
        is_following_project: true,
      },
    },
    events: {
      closeProjectSidebar: handleClose,
      unfollow: (assignment) => deleteAssignment(assignment),
    },
  },
  {
    title: t('project.labels.otherProjects'),
    props: {
      title: t('project.labels.otherProjects'),
      is_open: toggle_projects_other.value,
      is_fetching: is_fetching_other.value,
      on_toggle: toggleOtherProjects,
      projects: projects_other.value,
      item_props: {
        assignments: assignments.value,
        project_ids_follow_pending: project_ids_follow_pending.value,
      },
    },
    events: {
      closeProjectSidebar: handleClose,
      follow: (assignment) => createAssignment(assignment),
    },
  },
  {
    title: t('project.labels.templateProjects'),
    props: {
      title: t('project.labels.templateProjects'),
      is_open: toggle_projects_template.value,
      is_fetching: is_fetching_template.value,
      on_toggle: toggleProjectTemplates,
      projects: projects_template.value,
      item_props: { assignments: assignments.value, is_template: true },
    },
    events: {
      closeProjectSidebar: handleClose,
    },
  },
]);
</script>
